import React, { useEffect, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  SCHEDULE_WEEKDAY_HEADER_LABELS,
  dayOfMonthNumber,
  isDateInMonth,
  isoDate,
  monthCalendarWeeks,
  type MonthAnchor,
} from '../../sampling/surveyScheduleCalendar';
import {
  applyBeritaAcaraIssuedFlag,
  buildQuotationScheduleDaySummariesByDate,
  isActiveScheduleOnDetailCalendar,
  isScheduleCardCompleted,
  type QuotationScheduleDaySummary,
} from './quotationScheduleCalendarUtils';
import {
  buildMonthlySurveyTeamNumbersByRoster,
  DEFINED_SURVEY_TEAM_MIN_MEMBERS,
  formatSurveyMemberNames,
  resolveMonthlySurveyTeamNumber,
  surveyTeamMemberRosterKey,
} from './surveyTeamMonthLabel';

export type MonthlyQuotationTeamCalendarProps = {
  monthAnchor: MonthAnchor;
  teams: import('../../api/strapiDynamicEntity').DynamicEntityRow[];
  extraSummariesByDate?: Map<string, QuotationScheduleDaySummary[]>;
  currentScheduleDocumentId: string;
  /**
   * Quotation konsultan/kontrak: semua kartu group quotation ini berwarna biru (aktif).
   * Kosong = hanya `currentScheduleDocumentId` yang biru.
   */
  activeQuotationDocumentId?: string | null;
  /** `overview` = kartu hijau semua quotation (halaman index). */
  cardTone?: 'detail' | 'overview';
  /** Warna kartu: kuning = belum approve, merah = ditolak, hijau = approved, abu gelap = WO dibuat. */
  useScheduleStatusColors?: boolean;
  /** Work order yang semua titik berita acaranya sudah terbit → kartu abu. */
  issuedWorkOrderIds?: Set<string>;
  locked?: boolean;
  onQuotationClick?: (date: string, summary: QuotationScheduleDaySummary) => void;
  onAddClick?: (date: string) => void;
  /** Hapus slot penjadwalan pada tanggal kartu (draft lokal). */
  onDeleteScheduleClick?: (date: string, summary: QuotationScheduleDaySummary) => void;
  canDeleteSchedule?: (summary: QuotationScheduleDaySummary) => boolean;
  onEditScheduleClick?: (date: string, summary: QuotationScheduleDaySummary) => void;
  canEditSchedule?: (summary: QuotationScheduleDaySummary) => boolean;
  /** Lihat detail jadwal (read-only) untuk kartu non-aktif. */
  onViewScheduleClick?: (date: string, summary: QuotationScheduleDaySummary) => void;
  /**
   * Native HTML5 drag-and-drop: drop ke kartu/slot tim yang sudah ada.
   * `targetTeam` = template roster tim tujuan (anggota + jam).
   */
  onScheduleCardDrop?: (
    fromDate: string,
    toDate: string,
    dragged: QuotationScheduleDaySummary,
    targetTeam: QuotationScheduleDaySummary,
  ) => void;
  canDragSchedule?: (summary: QuotationScheduleDaySummary) => boolean;
  getDayTestId?: (date: string) => string | undefined;
  getAddTestId?: (date: string) => string | undefined;
  /** Stable root test id (toolbar monthly toggle is hidden on schedule detail). */
  rootTestId?: string;
  /** Daftar petugas sampling aktif untuk dropdown tambah petugas. */
  teamOfficerOptions?: SurveyTeamOfficerOption[];
  /** Apakah roster petugas pada kartu boleh diedit lewat modal footer. */
  canEditTeam?: (summary: QuotationScheduleDaySummary) => boolean;
  /** Simpan roster + nama tim untuk quotation dan tanggal kartu ini saja. */
  onSaveTeamMembers?: (
    date: string,
    summary: QuotationScheduleDaySummary,
    payload: SurveyTeamMembersSavePayload,
    cardSummaries: QuotationScheduleDaySummary[],
  ) => void;
  /** True sementara mutasi simpan tim berjalan. */
  teamMembersSaving?: boolean;
};

const SCHEDULE_CARD_DND_MIME = 'application/x-genaulab-schedule-card';

export type ScheduleCardDragPayload = {
  fromDate: string;
  scheduleDocumentId: string;
};

function encodeScheduleCardDragPayload(payload: ScheduleCardDragPayload): string {
  return JSON.stringify(payload);
}

function decodeScheduleCardDragPayload(raw: string): ScheduleCardDragPayload | null {
  try {
    const parsed = JSON.parse(raw) as ScheduleCardDragPayload;
    if (
      typeof parsed?.fromDate !== 'string' ||
      typeof parsed?.scheduleDocumentId !== 'string' ||
      !/^\d{4}-\d{2}-\d{2}$/.test(parsed.fromDate) ||
      !parsed.scheduleDocumentId.trim()
    ) {
      return null;
    }
    return {
      fromDate: parsed.fromDate,
      scheduleDocumentId: parsed.scheduleDocumentId.trim(),
    };
  } catch {
    return null;
  }
}

function readScheduleCardDragPayload(dataTransfer: DataTransfer): ScheduleCardDragPayload | null {
  const typed = dataTransfer.getData(SCHEDULE_CARD_DND_MIME);
  if (typed) return decodeScheduleCardDragPayload(typed);
  const plain = dataTransfer.getData('text/plain');
  return plain ? decodeScheduleCardDragPayload(plain) : null;
}

function isDefinedSurveyTeam(summary: QuotationScheduleDaySummary): boolean {
  return summary.members.length >= DEFINED_SURVEY_TEAM_MIN_MEMBERS;
}

/** Cari template roster untuk teamKey (dipakai slot kosong di tanggal lain). */
function findTeamTemplateSummary(
  teamKey: string,
  summariesByDate: Map<string, QuotationScheduleDaySummary[]>,
): QuotationScheduleDaySummary | null {
  for (const rows of summariesByDate.values()) {
    for (const row of rows) {
      if (summaryTeamKey(row) === teamKey && isDefinedSurveyTeam(row)) return row;
    }
  }
  return null;
}

function teamDropTargetId(date: string, teamKey: string): string {
  return `${date}::${teamKey}`;
}

function summaryTimeRangeLabel(summary: QuotationScheduleDaySummary): string {
  const rawLabels = summary.members.flatMap((member) => member.timeLabels ?? []);
  const normalized = [...new Set(rawLabels.map((label) => label.trim()).filter(Boolean))];
  if (normalized.length === 0) return 'Jam belum ditentukan';
  if (normalized.includes('Sepanjang hari')) return 'Sepanjang hari';

  const hhmm = normalized
    .filter((label) => /^\d{2}:\d{2}$/.test(label))
    .sort((a, b) => a.localeCompare(b, 'id'));
  if (hhmm.length === 0) return normalized.join(', ');
  if (hhmm.length === 1) return `Jam ${hhmm[0]}`;
  return `${hhmm[0]} - ${hhmm[hhmm.length - 1]}`;
}

/** Kunci kartu tim: kombinasi nama petugas yang sama masuk satu tim. Tanpa petugas, kartu tetap per jadwal. */
export function summaryTeamKey(summary: QuotationScheduleDaySummary): string {
  if (summary.members.length === 0) return `schedule:${summary.scheduleDocumentId}`;
  return surveyTeamMemberRosterKey(summary.members) ?? `schedule:${summary.scheduleDocumentId}`;
}

function groupSummariesByTeamKey(
  summaries: QuotationScheduleDaySummary[],
): Map<string, QuotationScheduleDaySummary[]> {
  const map = new Map<string, QuotationScheduleDaySummary[]>();
  summaries.forEach((summary) => {
    const key = summaryTeamKey(summary);
    const list = map.get(key) ?? [];
    list.push(summary);
    map.set(key, list);
  });
  return map;
}

/**
 * Urutan baris tim dalam satu minggu kalender.
 * Tim dengan nomor (Tim 1..N) diurutkan naik; sisanya (petugas belum lengkap) di bawahnya.
 * Dipakai agar slot kosong mengisi baris yang sama di tanggal lain.
 */
export function buildWeekTeamSlotOrder(
  weekDates: string[],
  summariesByDate: Map<string, QuotationScheduleDaySummary[]>,
  teamNumberByLookupKey: Map<string, number>,
): string[] {
  type Meta = { teamNumber: number | null };
  const metaByKey = new Map<string, Meta>();

  for (const date of weekDates) {
    for (const summary of summariesByDate.get(date) ?? []) {
      const key = summaryTeamKey(summary);
      const teamNumber = resolveMonthlySurveyTeamNumber(
        date,
        summary.members,
        teamNumberByLookupKey,
      );
      const existing = metaByKey.get(key);
      if (!existing) {
        metaByKey.set(key, { teamNumber });
        continue;
      }
      if (existing.teamNumber == null && teamNumber != null) {
        existing.teamNumber = teamNumber;
      }
    }
  }

  return [...metaByKey.entries()]
    .sort(([keyA, metaA], [keyB, metaB]) => {
      if (metaA.teamNumber != null && metaB.teamNumber != null) {
        if (metaA.teamNumber !== metaB.teamNumber) return metaA.teamNumber - metaB.teamNumber;
      } else if (metaA.teamNumber != null) {
        return -1;
      } else if (metaB.teamNumber != null) {
        return 1;
      }
      return keyA.localeCompare(keyB, 'id');
    })
    .map(([key]) => key);
}

type DetailHeaderTone = 'current' | 'completed' | 'scheduled';

function detailHeaderTone(opts: { isCurrent: boolean; completed: boolean }): DetailHeaderTone {
  if (opts.isCurrent) return 'current';
  if (opts.completed) return 'completed';
  return 'scheduled';
}

const DETAIL_HEADER = {
  current: {
    bgcolor: 'primary.main',
    borderColor: 'primary.main',
    color: 'primary.contrastText',
    hoverBg: 'primary.dark',
  },
  completed: {
    bgcolor: 'grey.600',
    borderColor: 'grey.600',
    color: 'grey.100',
    hoverBg: 'grey.700',
  },
  scheduled: {
    bgcolor: 'success.main',
    borderColor: 'success.dark',
    color: 'success.contrastText',
    hoverBg: 'success.dark',
  },
} as const;

function QuotationNotesBlock({
  notes,
  borderColor,
}: {
  notes: string;
  borderColor: string;
}) {
  const trimmed = notes.trim();
  if (!trimmed) return null;
  return (
    <Box
      sx={{
        px: 0.75,
        py: 0.55,
        borderTop: '1px solid',
        borderColor,
        bgcolor: 'warning.light',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          lineHeight: 1.25,
          fontSize: '0.6rem',
          color: 'text.primary',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          fontStyle: 'italic',
        }}
      >
        {trimmed}
      </Typography>
    </Box>
  );
}

function TransportasiNotesBlock({
  notes,
  borderColor,
}: {
  notes: string;
  borderColor: string;
}) {
  const trimmed = notes.trim();
  if (!trimmed) return null;
  return (
    <Box
      sx={{
        px: 0.75,
        py: 0.55,
        borderTop: '1px solid',
        borderColor,
        bgcolor: 'info.light',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          lineHeight: 1.25,
          fontSize: '0.6rem',
          color: 'text.primary',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}
      >
        Kendaraan: {trimmed}
      </Typography>
    </Box>
  );
}

/** Cadangan tinggi konten agar tidak tertutup footer tim absolut. */
const TEAM_STAFF_FOOTER_RESERVE_PX = 44;

function TeamStaffFooter({
  memberNames,
  hasSamplingStaff,
  canEdit = false,
  onEditTeam,
}: {
  memberNames: string;
  hasSamplingStaff: boolean;
  canEdit?: boolean;
  onEditTeam?: () => void;
}) {
  return (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        px: 0.5,
        py: 0.5,
        minHeight: 34,
        borderTop: 0,
        bgcolor: hasSamplingStaff ? 'warning.light' : 'common.white',
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, px: 0.25 }}>
        {hasSamplingStaff ? (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              lineHeight: 1.2,
              fontSize: '0.6rem',
              color: 'text.primary',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {memberNames}
          </Typography>
        ) : (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              lineHeight: 1.2,
              fontSize: '0.6rem',
              color: 'text.disabled',
              fontStyle: 'italic',
            }}
          >
            petugas sampling kosong
          </Typography>
        )}
      </Box>
      {onEditTeam ? (
        <Tooltip title={canEdit ? 'Edit petugas tim' : 'Lihat daftar petugas tim'}>
          <IconButton
            size="small"
            aria-label={canEdit ? 'Edit petugas tim' : 'Lihat daftar petugas tim'}
            data-testid="schedule-team-staff-edit"
            draggable={false}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEditTeam();
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDragStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            sx={{
              flexShrink: 0,
              width: 22,
              height: 22,
              pointerEvents: 'auto',
              bgcolor: 'rgba(0,0,0,0.2)',
              color: 'text.primary',
              '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
}

export type SurveyTeamOfficerOption = {
  userId: string;
  name: string;
};

export type SurveyTeamMembersSavePayload = {
  teamName: string;
  members: Array<{ userId: string; name: string; isLead: boolean }>;
};

function SurveyTeamMembersDialog({
  open,
  summary,
  teamNumber,
  officerOptions = [],
  canEdit = false,
  saving = false,
  onClose,
  onSave,
}: {
  open: boolean;
  summary: QuotationScheduleDaySummary | null;
  teamNumber?: number | null;
  officerOptions?: SurveyTeamOfficerOption[];
  canEdit?: boolean;
  saving?: boolean;
  onClose: () => void;
  onSave?: (payload: SurveyTeamMembersSavePayload) => void;
}) {
  const defaultTeamName =
    String(summary?.teamName ?? '').trim() ||
    (teamNumber != null && (summary?.members.length ?? 0) >= DEFINED_SURVEY_TEAM_MIN_MEMBERS
      ? `Tim ${teamNumber}`
      : '');
  const [teamName, setTeamName] = useState(defaultTeamName);
  const [members, setMembers] = useState<
    Array<{ userId: string; name: string; isLead: boolean }>
  >([]);
  const [addingOfficer, setAddingOfficer] = useState(false);
  const [pendingUserId, setPendingUserId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !summary) return;
    setTeamName(
      String(summary.teamName ?? '').trim() ||
        (teamNumber != null && summary.members.length >= DEFINED_SURVEY_TEAM_MIN_MEMBERS
          ? `Tim ${teamNumber}`
          : ''),
    );
    setMembers(
      summary.members.map((member) => ({
        userId: member.userId,
        name: member.name,
        isLead: Boolean(member.isLead),
      })),
    );
    setAddingOfficer(false);
    setPendingUserId('');
    setError(null);
  }, [open, summary, teamNumber]);

  const availableOfficers = useMemo(() => {
    const taken = new Set(members.map((member) => member.userId));
    return officerOptions.filter((row) => !taken.has(row.userId));
  }, [officerOptions, members]);

  if (!summary) return null;

  const titleLabel =
    String(teamName).trim() ||
    (teamNumber != null && members.length >= DEFINED_SURVEY_TEAM_MIN_MEMBERS
      ? `Tim ${teamNumber}`
      : 'Tim survey');

  function handleAddOfficer() {
    const userId = pendingUserId.trim();
    if (!userId) {
      setError('Pilih petugas sampling terlebih dahulu.');
      return;
    }
    const option = availableOfficers.find((row) => row.userId === userId);
    if (!option) {
      setError('Petugas tidak ditemukan pada daftar petugas sampling.');
      return;
    }
    setError(null);
    setMembers((prev) => {
      const nextLead = prev.length === 0;
      return [...prev, { userId: option.userId, name: option.name, isLead: nextLead }];
    });
    setPendingUserId('');
    setAddingOfficer(false);
  }

  function handleRemoveOfficer(userId: string) {
    setMembers((prev) => {
      const next = prev.filter((member) => member.userId !== userId);
      if (next.length > 0 && !next.some((member) => member.isLead)) {
        next[0] = { ...next[0]!, isLead: true };
      }
      return next;
    });
  }

  function handleToggleLead(userId: string) {
    setMembers((prev) =>
      prev.map((member) => ({
        ...member,
        isLead: member.userId === userId,
      })),
    );
  }

  function handleSave() {
    setError(null);
    if (members.length < DEFINED_SURVEY_TEAM_MIN_MEMBERS) {
      setError('Tambah minimal dua petugas.');
      return;
    }
    if (!members.some((member) => member.isLead)) {
      setError('Minimal satu petugas harus ditandai sebagai lead.');
      return;
    }
    onSave?.({
      teamName: teamName.trim(),
      members,
    });
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      data-testid="schedule-team-members-dialog"
    >
      <DialogTitle>{canEdit ? 'Edit petugas tim' : titleLabel}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {summary.quotationNumber} · {summary.customerName || '—'}
          </Typography>

          {canEdit ? (
            <TextField
              label="Nama tim"
              size="small"
              fullWidth
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Contoh: Tim A / Tim Pagi"
              disabled={saving}
              inputProps={{ 'data-testid': 'schedule-team-members-name' }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Lead: {summary.teamLeadName} · {summary.teamCount} petugas
            </Typography>
          )}

          {canEdit ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                Petugas terpilih
              </Typography>
              {members.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  data-testid="schedule-team-members-list-empty"
                >
                  Belum ada petugas. Klik tambah petugas untuk memilih dari daftar.
                </Typography>
              ) : (
                <List dense disablePadding data-testid="schedule-team-members-list">
                  {members.map((member) => (
                    <ListItem
                      key={member.userId}
                      sx={{ px: 0, gap: 0.5 }}
                      divider
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label={`Hapus ${member.name}`}
                          disabled={saving}
                          onClick={() => handleRemoveOfficer(member.userId)}
                          data-testid={`schedule-team-members-remove-${member.userId}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={member.name}
                        secondary={
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={member.isLead}
                                disabled={saving}
                                onChange={(_, checked) => {
                                  if (checked) handleToggleLead(member.userId);
                                }}
                              />
                            }
                            label="Lead"
                          />
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {addingOfficer ? (
                <Stack spacing={1} sx={{ mt: 1.25 }}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Petugas sampling"
                    value={pendingUserId}
                    onChange={(e) => setPendingUserId(e.target.value)}
                    disabled={saving || availableOfficers.length === 0}
                    SelectProps={{
                      displayEmpty: true,
                    }}
                    inputProps={{ 'data-testid': 'schedule-team-members-add-select' }}
                  >
                    <MenuItem value="">
                      <em>
                        {availableOfficers.length === 0
                          ? 'Semua petugas sudah dipilih'
                          : 'Pilih petugas…'}
                      </em>
                    </MenuItem>
                    {availableOfficers.map((row) => (
                      <MenuItem key={row.userId} value={row.userId}>
                        {row.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      onClick={() => {
                        setAddingOfficer(false);
                        setPendingUserId('');
                        setError(null);
                      }}
                      disabled={saving}
                    >
                      Batal
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleAddOfficer}
                      disabled={saving || !pendingUserId}
                      data-testid="schedule-team-members-add-confirm"
                    >
                      Tambahkan
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  sx={{ mt: 1.25 }}
                  onClick={() => {
                    setError(null);
                    setAddingOfficer(true);
                  }}
                  disabled={saving || availableOfficers.length === 0}
                  data-testid="schedule-team-members-add"
                >
                  Tambah petugas
                </Button>
              )}
            </Box>
          ) : members.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Belum ada petugas pada tim ini.
            </Typography>
          ) : (
            <List dense disablePadding data-testid="schedule-team-members-list">
              {members.map((member) => (
                <ListItem key={member.userId} sx={{ px: 0 }} divider>
                  <ListItemText
                    primary={`${member.name}${member.isLead ? ' (Lead)' : ''}`}
                    secondary={
                      summary.members.find((row) => row.userId === member.userId)?.timeLabels.join(
                        ', ',
                      ) || 'Sepanjang hari'
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          {error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      {!canEdit || members.length >= DEFINED_SURVEY_TEAM_MIN_MEMBERS ? (
        <DialogActions>
          <Button onClick={onClose} disabled={saving} data-testid="schedule-team-members-close">
            {canEdit ? 'Batal' : 'Tutup'}
          </Button>
          {canEdit ? (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              data-testid="schedule-team-members-save"
            >
              {saving ? 'Menyimpan…' : 'Simpan'}
            </Button>
          ) : null}
        </DialogActions>
      ) : null}
    </Dialog>
  );
}

function ContractScheduleIdentity({ summary }: { summary: QuotationScheduleDaySummary }) {
  if (!summary.isContract) return null;
  const group = summary.groupLabel?.trim() ?? '';
  const eventIndex = summary.recurringEventIndex;
  const hasEvent = eventIndex != null && Number.isFinite(eventIndex);
  if (!group && !hasEvent) return null;
  return (
    <>
      {group ? (
        <Typography
          variant="caption"
          sx={{ display: 'block', lineHeight: 1.2, opacity: 0.95, fontSize: '0.64rem' }}
        >
          {group}
        </Typography>
      ) : null}
      {hasEvent ? (
        <Typography
          variant="caption"
          sx={{ display: 'block', lineHeight: 1.2, opacity: 0.95, fontSize: '0.64rem' }}
        >
          Jadwal ke-{eventIndex}
        </Typography>
      ) : null}
    </>
  );
}

function QuotationDayCard({
  summary,
  isCurrent,
  isFocused = isCurrent,
  cardTone = 'detail',
  useScheduleStatusColors = false,
  teamNumber = null,
  onClick,
  onEdit,
  onView,
  onEditTeam,
  onDelete,
  dragEnabled = false,
  onDragStart,
  onDragEnd,
}: {
  summary: QuotationScheduleDaySummary;
  isCurrent: boolean;
  /** Jadwal yang sedang dibuka (testid current-card). Default sama dengan isCurrent. */
  isFocused?: boolean;
  cardTone?: 'detail' | 'overview';
  useScheduleStatusColors?: boolean;
  teamNumber?: number | null;
  onClick?: () => void;
  onEdit?: () => void;
  onView?: () => void;
  /** Edit daftar petugas di footer tim (bukan edit jadwal header). */
  onEditTeam?: () => void;
  onDelete?: () => void;
  dragEnabled?: boolean;
  onDragStart?: (event: React.DragEvent) => void;
  onDragEnd?: (event: React.DragEvent) => void;
}) {
  if (cardTone === 'detail') {
    const memberNames = formatSurveyMemberNames(summary.members, teamNumber, summary.teamName);
    const hasSamplingStaff = summary.members.length > 0;
    const headerLabel = summary.customerName?.trim() || '—';
    const headerTone = detailHeaderTone({
      isCurrent,
      completed: isScheduleCardCompleted(summary),
    });
    const header = DETAIL_HEADER[headerTone];
    const samplingTimeRange = summaryTimeRangeLabel(summary);
    const actionBtnSx = {
      zIndex: 2,
      width: 22,
      height: 22,
      pointerEvents: 'auto' as const,
      bgcolor: 'rgba(0,0,0,0.28)',
      color: 'common.white',
      '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
    };
    return (
      <Box
        data-testid={
          isFocused ? 'schedule-quotation-current-card' : `schedule-quotation-card-${summary.scheduleDocumentId}`
        }
        data-schedule-draggable={dragEnabled ? 'true' : 'false'}
        draggable={dragEnabled}
        onDragStart={dragEnabled ? onDragStart : undefined}
        onDragEnd={dragEnabled ? onDragEnd : undefined}
        onClick={onClick}
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          textAlign: 'left',
          border: isCurrent ? '2px solid' : '1px solid',
          borderColor: isCurrent ? 'primary.main' : header.borderColor,
          borderRadius: 1,
          p: 0,
          pb: `${TEAM_STAFF_FOOTER_RESERVE_PX}px`,
          overflow: 'hidden',
          cursor: dragEnabled ? 'grab' : onClick ? 'pointer' : 'default',
          bgcolor: header.bgcolor,
          WebkitUserDrag: dragEnabled ? 'element' : 'auto',
          userSelect: dragEnabled ? 'none' : 'auto',
          '&:active': dragEnabled ? { cursor: 'grabbing' } : undefined,
          ...(isCurrent
            ? {
                boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.light}`,
              }
            : {}),
        }}
      >
        {onEdit || onView || onDelete ? (
          <Stack
            direction="row"
            spacing={0.25}
            sx={{ position: 'absolute', top: 2, right: 2, zIndex: 2 }}
          >
            {onEdit ? (
              <Tooltip title="Edit jadwal">
                <IconButton
                  size="small"
                  aria-label="Edit jadwal"
                  data-testid="schedule-quotation-card-edit"
                  draggable={false}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onEdit();
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDragStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  sx={actionBtnSx}
                >
                  <EditOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            ) : onView ? (
              <Tooltip title="Lihat jadwal">
                <IconButton
                  size="small"
                  aria-label="Lihat jadwal"
                  data-testid="schedule-quotation-card-view"
                  draggable={false}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onView();
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDragStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  sx={actionBtnSx}
                >
                  <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            ) : null}
            {onDelete ? (
              <Tooltip title="Hapus tanggal penjadwalan">
                <IconButton
                  size="small"
                  aria-label="Hapus tanggal penjadwalan"
                  data-testid="schedule-quotation-card-delete"
                  draggable={false}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDelete();
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDragStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  sx={{
                    ...actionBtnSx,
                    '&:hover': { bgcolor: 'error.main', color: 'error.contrastText' },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        ) : null}
        <Box
          sx={{
            width: '100%',
            textAlign: 'left',
            border: 0,
            px: 0.75,
            py: 0.6,
            pr: onEdit || onView || onDelete ? 5.5 : 0.75,
            bgcolor: header.bgcolor,
            color: header.color,
            // Event mouse diarahkan ke parent agar HTML5 drag andal.
            pointerEvents: 'none',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.2 }}>
            {headerLabel}
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: 'block', lineHeight: 1.2, opacity: 0.95, fontSize: '0.64rem' }}
          >
            {summary.quotationNumber}
          </Typography>
          {summary.scheduleDocumentId?.trim() ? (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                lineHeight: 1.2,
                opacity: 0.95,
                fontSize: '0.58rem',
                wordBreak: 'break-all',
              }}
            >
              ID: {summary.scheduleDocumentId.trim()}
            </Typography>
          ) : null}
          <ContractScheduleIdentity summary={summary} />
          <Typography
            variant="caption"
            sx={{ display: 'block', lineHeight: 1.2, opacity: 0.95, fontSize: '0.64rem' }}
          >
            {samplingTimeRange}
          </Typography>
          {summary.measurementTypesLabel?.trim() ? (
            <Typography
              variant="caption"
              sx={{ display: 'block', lineHeight: 1.2, opacity: 0.95, fontSize: '0.64rem' }}
            >
              {summary.measurementTypesLabel.trim()}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ pointerEvents: 'none' }}>
          <QuotationNotesBlock
            notes={summary.notes ?? ''}
            borderColor={isCurrent ? 'primary.dark' : 'warning.dark'}
          />
          <TransportasiNotesBlock
            notes={summary.transportasiNote ?? ''}
            borderColor={isCurrent ? 'primary.dark' : 'info.dark'}
          />
        </Box>
        <Box sx={{ pointerEvents: 'none' }}>
          <TeamStaffFooter
            memberNames={memberNames}
            hasSamplingStaff={hasSamplingStaff}
            canEdit={Boolean(onEditTeam)}
            onEditTeam={onEditTeam}
          />
        </Box>
      </Box>
    );
  }

  const woCreated = summary.scheduleStatus === 'work_order_created';
  const sampledReceived = Boolean(String(summary.samplingReceivedAt ?? '').trim());
  const approved = summary.scheduleStatus === 'approved';
  const rejected = summary.scheduleStatus === 'rejected';
  const otherSchedulePalette = {
    bgcolor: 'grey.300',
    borderColor: 'grey.500',
    color: 'text.primary',
  };
  const pendingPalette = {
    bgcolor: 'warning.main',
    borderColor: 'warning.dark',
    color: 'warning.contrastText',
  };
  const approvedPalette = {
    bgcolor: 'success.main',
    borderColor: 'success.dark',
    color: 'success.contrastText',
  };
  const rejectedPalette = {
    bgcolor: 'error.main',
    borderColor: 'error.dark',
    color: 'error.contrastText',
  };
  const woCreatedPalette = {
    bgcolor: 'grey.700',
    borderColor: 'grey.900',
    color: 'grey.100',
  };
  const statusPalette = sampledReceived
    ? otherSchedulePalette
    : woCreated
    ? woCreatedPalette
    : approved
      ? approvedPalette
      : rejected
        ? rejectedPalette
        : pendingPalette;
  const palette = useScheduleStatusColors
    ? cardTone === 'overview'
      ? statusPalette
      : isCurrent
        ? statusPalette
        : otherSchedulePalette
    : {
        bgcolor: cardTone === 'overview' ? 'success.main' : isCurrent ? 'primary.main' : 'grey.200',
        borderColor: cardTone === 'overview' ? 'success.dark' : isCurrent ? 'primary.dark' : 'grey.400',
        color:
          cardTone === 'overview' ? 'success.contrastText' : isCurrent ? 'primary.contrastText' : 'text.primary',
      };
  const content = (
    <Box
      component={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-testid={
        isFocused ? 'schedule-quotation-current-card' : `schedule-quotation-card-${summary.scheduleDocumentId}`
      }
      sx={{
        width: '100%',
        textAlign: 'left',
        border: 0,
        borderRadius: 0.75,
        p: 0.5,
        cursor: onClick ? 'pointer' : 'default',
        bgcolor: palette.bgcolor,
        color: palette.color,
        '&:hover': onClick ? { filter: 'brightness(0.95)' } : undefined,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.2 }}>
        {summary.groupLabel ?? summary.quotationNumber}
      </Typography>
      {summary.groupLabel ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            lineHeight: 1.2,
            opacity: 0.92,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {summary.quotationNumber}
          {summary.scheduleNumber?.trim() ? ` · ${summary.scheduleNumber.trim()}` : ''}
        </Typography>
      ) : summary.scheduleNumber?.trim() &&
        summary.scheduleNumber.trim() !== summary.quotationNumber.trim() ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            lineHeight: 1.2,
            opacity: 0.92,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {summary.scheduleNumber.trim()}
        </Typography>
      ) : null}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          lineHeight: 1.2,
          opacity: 0.95,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {summary.customerName || '—'}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          lineHeight: 1.25,
          fontSize: '0.6rem',
          opacity: 0.9,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}
      >
        {formatSurveyMemberNames(summary.members, teamNumber, summary.teamName)}
      </Typography>
      {isCurrent && summary.notes && !useScheduleStatusColors ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            lineHeight: 1.2,
            fontSize: '0.55rem',
            opacity: 0.85,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            fontStyle: 'italic',
          }}
        >
          {summary.notes}
        </Typography>
      ) : null}
      {summary.transportasiNote?.trim() ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            lineHeight: 1.2,
            fontSize: '0.55rem',
            opacity: 0.9,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          Kendaraan: {summary.transportasiNote.trim()}
        </Typography>
      ) : null}
    </Box>
  );

  if (onClick) {
    const tooltip =
      cardTone === 'overview'
        ? 'Klik untuk kelola penjadwalan quotation'
        : isCurrent
          ? woCreated || approved
            ? 'Klik untuk detail penjadwalan'
            : 'Klik untuk edit tim survey'
          : 'Klik untuk detail tim survey';
    return (
      <Tooltip title={tooltip}>
        {content}
      </Tooltip>
    );
  }
  return content;
}

function GroupedTeamDayCard({
  summaries,
  currentScheduleDocumentId,
  activeQuotationDocumentId,
  teamNumber = null,
  onQuotationClick,
  onEditSchedule,
  onViewSchedule,
  onEditTeam,
  onDeleteSchedule,
  canDeleteSchedule,
  canEditSchedule,
  canDragSchedule,
  onCardDragStart,
  onCardDragEnd,
  dropEnabled = false,
  dropActive = false,
  onDragOverTeam,
  onDragLeaveTeam,
  onDropOnTeam,
}: {
  summaries: QuotationScheduleDaySummary[];
  currentScheduleDocumentId: string;
  activeQuotationDocumentId?: string | null;
  teamNumber?: number | null;
  onQuotationClick?: (summary: QuotationScheduleDaySummary) => void;
  onEditSchedule?: (summary: QuotationScheduleDaySummary) => void;
  onViewSchedule?: (summary: QuotationScheduleDaySummary) => void;
  onEditTeam?: (
    summary: QuotationScheduleDaySummary,
    cardSummaries: QuotationScheduleDaySummary[],
  ) => void;
  onDeleteSchedule?: (summary: QuotationScheduleDaySummary) => void;
  canDeleteSchedule?: (summary: QuotationScheduleDaySummary) => boolean;
  canEditSchedule?: (summary: QuotationScheduleDaySummary) => boolean;
  canDragSchedule?: (summary: QuotationScheduleDaySummary) => boolean;
  onCardDragStart?: (event: React.DragEvent, summary: QuotationScheduleDaySummary) => void;
  onCardDragEnd?: (event: React.DragEvent) => void;
  dropEnabled?: boolean;
  dropActive?: boolean;
  onDragOverTeam?: (event: React.DragEvent) => void;
  onDragLeaveTeam?: (event: React.DragEvent) => void;
  onDropOnTeam?: (event: React.DragEvent) => void;
}) {
  const dropHandlers = dropEnabled
    ? {
        onDragOver: onDragOverTeam,
        onDragLeave: onDragLeaveTeam,
        onDrop: onDropOnTeam,
      }
    : {};
  const dropHighlightSx = dropActive
    ? {
        outline: '2px solid',
        outlineColor: 'primary.main',
        outlineOffset: 1,
        boxShadow: (theme: { palette: { primary: { main: string } } }) =>
          `0 0 0 3px ${theme.palette.primary.main}55`,
      }
    : {};

  if (summaries.length === 1) {
    const summary = summaries[0];
    const showDelete = Boolean(canDeleteSchedule?.(summary) && onDeleteSchedule);
    const showEdit = Boolean(canEditSchedule?.(summary) && onEditSchedule);
    const showView = Boolean(!showEdit && onViewSchedule);
    const isCurrent = isActiveScheduleOnDetailCalendar(
      summary,
      currentScheduleDocumentId,
      activeQuotationDocumentId,
    );
    const dragEnabled = Boolean(canDragSchedule?.(summary) && onCardDragStart);
    return (
      <Box {...dropHandlers} sx={{ height: '100%', borderRadius: 1, ...dropHighlightSx }}>
        <QuotationDayCard
          summary={summary}
          isCurrent={isCurrent}
          isFocused={summary.scheduleDocumentId === currentScheduleDocumentId}
          cardTone="detail"
          useScheduleStatusColors={false}
          teamNumber={teamNumber}
          onClick={onQuotationClick ? () => onQuotationClick(summary) : undefined}
          onEdit={showEdit ? () => onEditSchedule?.(summary) : undefined}
          onView={showView ? () => onViewSchedule?.(summary) : undefined}
          onEditTeam={onEditTeam ? () => onEditTeam(summary, [summary]) : undefined}
          onDelete={showDelete ? () => onDeleteSchedule?.(summary) : undefined}
          dragEnabled={dragEnabled}
          onDragStart={onCardDragStart ? (event) => onCardDragStart(event, summary) : undefined}
          onDragEnd={onCardDragEnd}
        />
      </Box>
    );
  }

  const orderedSummaries = [...summaries].sort((a, b) => {
    const aFocused = a.scheduleDocumentId === currentScheduleDocumentId ? 0 : 1;
    const bFocused = b.scheduleDocumentId === currentScheduleDocumentId ? 0 : 1;
    if (aFocused !== bFocused) return aFocused - bFocused;
    const eventA = a.recurringEventIndex ?? Number.MAX_SAFE_INTEGER;
    const eventB = b.recurringEventIndex ?? Number.MAX_SAFE_INTEGER;
    if (eventA !== eventB) return eventA - eventB;
    return (a.groupLabel ?? '').localeCompare(b.groupLabel ?? '', 'id');
  });
  const members = orderedSummaries[0]?.members ?? [];
  const hasSamplingStaff = members.length > 0;
  const memberNames = formatSurveyMemberNames(members, teamNumber, orderedSummaries[0]?.teamName);
  const teamRosterSummary =
    orderedSummaries.find((row) => row.scheduleDocumentId === currentScheduleDocumentId) ??
    orderedSummaries.find((row) => canEditSchedule?.(row)) ??
    orderedSummaries[0];
  const groupHasCurrent = orderedSummaries.some((summary) =>
    isActiveScheduleOnDetailCalendar(summary, currentScheduleDocumentId, activeQuotationDocumentId),
  );
  const allCompleted = orderedSummaries.every((summary) => isScheduleCardCompleted(summary));
  const groupTone = detailHeaderTone({ isCurrent: groupHasCurrent, completed: allCompleted });
  const groupHeader = DETAIL_HEADER[groupTone];

  return (
    <Box
      {...dropHandlers}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        border: groupHasCurrent ? '2px solid' : '1px solid',
        borderColor: groupHasCurrent ? 'primary.main' : groupHeader.borderColor,
        borderRadius: 1,
        p: 0,
        pb: `${TEAM_STAFF_FOOTER_RESERVE_PX}px`,
        overflow: 'hidden',
        bgcolor: groupHeader.bgcolor,
        ...(groupHasCurrent
          ? {
              boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.light}`,
            }
          : {}),
        ...dropHighlightSx,
      }}
    >
      <Box>
        {orderedSummaries.map((summary, index) => {
          const isFocused = summary.scheduleDocumentId === currentScheduleDocumentId;
          const isActive = isActiveScheduleOnDetailCalendar(
            summary,
            currentScheduleDocumentId,
            activeQuotationDocumentId,
          );
          const rowTone = detailHeaderTone({
            isCurrent: isActive,
            completed: isScheduleCardCompleted(summary),
          });
          const rowHeader = DETAIL_HEADER[rowTone];
          const showDelete = Boolean(canDeleteSchedule?.(summary) && onDeleteSchedule);
          const showEdit = Boolean(canEditSchedule?.(summary) && onEditSchedule);
          const showView = Boolean(!showEdit && onViewSchedule);
          const dragEnabled = Boolean(canDragSchedule?.(summary) && onCardDragStart);
          return (
            <Box
              key={summary.scheduleDocumentId}
              data-testid={
                isFocused
                  ? 'schedule-quotation-current-card'
                  : `schedule-quotation-card-${summary.scheduleDocumentId}`
              }
              data-schedule-draggable={dragEnabled ? 'true' : 'false'}
              draggable={dragEnabled}
              onDragStart={
                dragEnabled && onCardDragStart
                  ? (e) => {
                      onCardDragStart(e, summary);
                    }
                  : undefined
              }
              onDragEnd={dragEnabled ? onCardDragEnd : undefined}
              onClick={onQuotationClick ? () => onQuotationClick(summary) : undefined}
              sx={{
                position: 'relative',
                cursor: dragEnabled ? 'grab' : onQuotationClick ? 'pointer' : 'default',
                WebkitUserDrag: dragEnabled ? 'element' : 'auto',
                userSelect: dragEnabled ? 'none' : 'auto',
                '&:active': dragEnabled ? { cursor: 'grabbing' } : undefined,
              }}
            >
              {showEdit || showView || showDelete ? (
                <Stack
                  direction="row"
                  spacing={0.25}
                  sx={{ position: 'absolute', top: 2, right: 2, zIndex: 2 }}
                >
                  {showEdit ? (
                    <Tooltip title="Edit jadwal">
                      <IconButton
                        size="small"
                        aria-label="Edit jadwal"
                        data-testid="schedule-quotation-card-edit"
                        draggable={false}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onEditSchedule?.(summary);
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onDragStart={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        sx={{
                          width: 22,
                          height: 22,
                          pointerEvents: 'auto',
                          bgcolor: 'rgba(0,0,0,0.28)',
                          color: 'common.white',
                          '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
                        }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  ) : showView ? (
                    <Tooltip title="Lihat jadwal">
                      <IconButton
                        size="small"
                        aria-label="Lihat jadwal"
                        data-testid="schedule-quotation-card-view"
                        draggable={false}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onViewSchedule?.(summary);
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onDragStart={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        sx={{
                          width: 22,
                          height: 22,
                          pointerEvents: 'auto',
                          bgcolor: 'rgba(0,0,0,0.28)',
                          color: 'common.white',
                          '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
                        }}
                      >
                        <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  {showDelete ? (
                    <Tooltip title="Hapus tanggal penjadwalan">
                      <IconButton
                        size="small"
                        aria-label="Hapus tanggal penjadwalan"
                        data-testid="schedule-quotation-card-delete"
                        draggable={false}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onDeleteSchedule?.(summary);
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onDragStart={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        sx={{
                          width: 22,
                          height: 22,
                          pointerEvents: 'auto',
                          bgcolor: 'rgba(0,0,0,0.28)',
                          color: 'common.white',
                          '&:hover': { bgcolor: 'error.main', color: 'error.contrastText' },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Stack>
              ) : null}
              <Box
                sx={{
                  width: '100%',
                  textAlign: 'left',
                  border: 0,
                  bgcolor: rowHeader.bgcolor,
                  color: rowHeader.color,
                  px: 0.75,
                  pr: showEdit || showView || showDelete ? 5.5 : 0.75,
                  py: 0.6,
                  pointerEvents: 'none',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.2 }}>
                  {summary.customerName || '—'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', lineHeight: 1.2, opacity: 0.95, fontSize: '0.64rem' }}
                >
                  {summary.quotationNumber}
                </Typography>
                {summary.scheduleDocumentId?.trim() ? (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      lineHeight: 1.2,
                      opacity: 0.95,
                      fontSize: '0.58rem',
                      wordBreak: 'break-all',
                    }}
                  >
                    ID: {summary.scheduleDocumentId.trim()}
                  </Typography>
                ) : null}
                <ContractScheduleIdentity summary={summary} />
                <Typography
                  variant="caption"
                  sx={{ display: 'block', lineHeight: 1.2, opacity: 0.95, fontSize: '0.64rem' }}
                >
                  {summaryTimeRangeLabel(summary)}
                </Typography>
                {summary.measurementTypesLabel?.trim() ? (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', lineHeight: 1.2, opacity: 0.95, fontSize: '0.64rem' }}
                  >
                    {summary.measurementTypesLabel.trim()}
                  </Typography>
                ) : null}
              </Box>
              <Box sx={{ pointerEvents: 'none' }}>
                <QuotationNotesBlock
                  notes={summary.notes ?? ''}
                  borderColor={isActive ? 'primary.dark' : 'warning.dark'}
                />
                <TransportasiNotesBlock
                  notes={summary.transportasiNote ?? ''}
                  borderColor={isActive ? 'primary.dark' : 'info.dark'}
                />
              </Box>
              {index < summaries.length - 1 ? (
                <Box sx={{ height: 1, bgcolor: 'grey.300', opacity: 0.9 }} />
              ) : null}
            </Box>
          );
        })}
      </Box>
      <TeamStaffFooter
        memberNames={memberNames}
        hasSamplingStaff={hasSamplingStaff}
        canEdit={Boolean(onEditTeam && teamRosterSummary)}
        onEditTeam={
          onEditTeam && teamRosterSummary
            ? () => onEditTeam(teamRosterSummary, summaries)
            : undefined
        }
      />
    </Box>
  );
}

function MonthDayCell({
  date,
  inMonth,
  isToday,
  summaries,
  currentScheduleDocumentId,
  activeQuotationDocumentId,
  cardTone = 'detail',
  useScheduleStatusColors = false,
  teamNumberByLookupKey,
  weekTeamOrder,
  dayColumnIndex,
  useTeamRowAlign,
  locked,
  dropTargetId,
  onQuotationClick,
  onAddClick,
  onDeleteSchedule,
  canDeleteSchedule,
  onEditSchedule,
  canEditSchedule,
  onViewSchedule,
  canDragSchedule,
  onCardDragStart,
  onCardDragEnd,
  onDragOverTeam,
  onDragLeaveTeam,
  onDropOnTeam,
  resolveTeamDropTemplate,
  dayTestId,
  addTestId,
  onEditTeam,
}: {
  date: string;
  inMonth: boolean;
  isToday: boolean;
  summaries: QuotationScheduleDaySummary[];
  currentScheduleDocumentId: string;
  activeQuotationDocumentId?: string | null;
  cardTone?: 'detail' | 'overview';
  useScheduleStatusColors?: boolean;
  teamNumberByLookupKey: Map<string, number>;
  weekTeamOrder: string[];
  dayColumnIndex: number;
  useTeamRowAlign: boolean;
  locked?: boolean;
  dropTargetId?: string | null;
  onQuotationClick?: (summary: QuotationScheduleDaySummary) => void;
  onAddClick?: () => void;
  onDeleteSchedule?: (summary: QuotationScheduleDaySummary) => void;
  canDeleteSchedule?: (summary: QuotationScheduleDaySummary) => boolean;
  onEditSchedule?: (summary: QuotationScheduleDaySummary) => void;
  canEditSchedule?: (summary: QuotationScheduleDaySummary) => boolean;
  onViewSchedule?: (summary: QuotationScheduleDaySummary) => void;
  canDragSchedule?: (summary: QuotationScheduleDaySummary) => boolean;
  onCardDragStart?: (event: React.DragEvent, summary: QuotationScheduleDaySummary) => void;
  onCardDragEnd?: (event: React.DragEvent) => void;
  onDragOverTeam?: (event: React.DragEvent, targetId: string) => void;
  onDragLeaveTeam?: (event: React.DragEvent, targetId: string) => void;
  onDropOnTeam?: (
    event: React.DragEvent,
    toDate: string,
    targetTeam: QuotationScheduleDaySummary,
  ) => void;
  resolveTeamDropTemplate?: (teamKey: string) => QuotationScheduleDaySummary | null;
  dayTestId?: string;
  addTestId?: string;
  onEditTeam?: (
    summary: QuotationScheduleDaySummary,
    cardSummaries: QuotationScheduleDaySummary[],
  ) => void;
}) {
  const showAdd = inMonth && !locked && Boolean(onAddClick);
  const groupsByTeamKey = useMemo(() => groupSummariesByTeamKey(summaries), [summaries]);

  function teamNumberFor(summary: QuotationScheduleDaySummary): number | null {
    return resolveMonthlySurveyTeamNumber(date, summary.members, teamNumberByLookupKey);
  }

  function teamDropProps(teamKey: string, group: QuotationScheduleDaySummary[] | undefined) {
    const template =
      group?.find((row) => isDefinedSurveyTeam(row)) ?? resolveTeamDropTemplate?.(teamKey) ?? null;
    const dropEnabled = Boolean(inMonth && template && onDropOnTeam);
    const targetId = teamDropTargetId(date, teamKey);
    if (!dropEnabled || !template) {
      return {
        dropEnabled: false,
        dropActive: false,
        onDragOverTeam: undefined,
        onDragLeaveTeam: undefined,
        onDropOnTeam: undefined,
      };
    }
    return {
      dropEnabled: true,
      dropActive: dropTargetId === targetId,
      onDragOverTeam: onDragOverTeam ? (event: React.DragEvent) => onDragOverTeam(event, targetId) : undefined,
      onDragLeaveTeam: onDragLeaveTeam
        ? (event: React.DragEvent) => onDragLeaveTeam(event, targetId)
        : undefined,
      onDropOnTeam: onDropOnTeam
        ? (event: React.DragEvent) => onDropOnTeam(event, date, template)
        : undefined,
    };
  }

  const addButton =
    showAdd ? (
      <IconButton
        className="schedule-day-add-btn"
        size="small"
        title="Tambah slot quotation"
        aria-label={`Tambah slot quotation ${date}`}
        onClick={(e) => {
          e.stopPropagation();
          onAddClick?.();
        }}
        data-testid={addTestId}
        sx={{
          position: 'absolute',
          right: 2,
          bottom: 2,
          // Always visible — hover-only opacity caused Cypress detach races on remount.
          opacity: 1,
          pointerEvents: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 1,
          width: 24,
          height: 24,
          zIndex: 1,
          '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
        }}
      >
        <AddIcon sx={{ fontSize: 16 }} />
      </IconButton>
    ) : null;

  const dateHeader = (
    <Box
      sx={{
        width: 22,
        height: 22,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        aspectRatio: '1 / 1',
        boxSizing: 'border-box',
        ...(isToday && inMonth ? { bgcolor: 'primary.main', color: 'primary.contrastText' } : {}),
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: summaries.length > 0 && inMonth ? 700 : 500,
          lineHeight: 1,
          color: isToday && inMonth ? 'inherit' : inMonth ? 'text.primary' : 'text.disabled',
        }}
      >
        {dayOfMonthNumber(date)}
      </Typography>
    </Box>
  );

  const teamCardProps = {
    currentScheduleDocumentId,
    activeQuotationDocumentId,
    onQuotationClick,
    onDeleteSchedule,
    canDeleteSchedule,
    onEditSchedule,
    onViewSchedule,
    onEditTeam,
    canEditSchedule,
    canDragSchedule,
    onCardDragStart,
    onCardDragEnd,
  };

  if (useTeamRowAlign) {
    return (
      <Box
        data-testid={dayTestId}
        sx={{
          position: 'relative',
          gridColumn: dayColumnIndex,
          gridRow: '1 / -1',
          display: 'grid',
          gridTemplateRows: 'subgrid',
          minHeight: 120,
          borderTop: 1,
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: inMonth ? 'background.paper' : 'grey.50',
          px: 0.5,
          pt: 0.5,
          pb: showAdd ? 3 : 0.5,
          opacity: inMonth ? 1 : 0.55,
          '&:last-of-type': { borderRight: 0 },
        }}
      >
        <Box sx={{ minHeight: 22 }}>
          {dateHeader}
          {addButton}
        </Box>
        {weekTeamOrder.map((teamKey) => {
          const group = inMonth ? groupsByTeamKey.get(teamKey) : undefined;
          const dropProps = teamDropProps(teamKey, group);
          if (!group?.length) {
            return (
              <Box
                key={`team-slot-empty-${date}-${teamKey}`}
                aria-hidden={!dropProps.dropEnabled}
                {...(dropProps.dropEnabled
                  ? {
                      onDragOver: dropProps.onDragOverTeam,
                      onDragLeave: dropProps.onDragLeaveTeam,
                      onDrop: dropProps.onDropOnTeam,
                    }
                  : {})}
                sx={{
                  minHeight: dropProps.dropEnabled ? 40 : 0,
                  borderRadius: 1,
                  ...(dropProps.dropActive
                    ? {
                        outline: '2px dashed',
                        outlineColor: 'primary.main',
                        bgcolor: 'action.selected',
                      }
                    : dropProps.dropEnabled
                      ? {
                          border: '1px dashed',
                          borderColor: 'divider',
                        }
                      : {}),
                }}
              />
            );
          }
          return (
            <GroupedTeamDayCard
              key={`team-group-${date}-${teamKey}`}
              summaries={group}
              teamNumber={group[0] ? teamNumberFor(group[0]) : null}
              {...teamCardProps}
              {...dropProps}
            />
          );
        })}
      </Box>
    );
  }

  return (
    <Box
      data-testid={dayTestId}
      sx={{
        position: 'relative',
        minHeight: 120,
        borderTop: 1,
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: inMonth ? 'background.paper' : 'grey.50',
        p: 0.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        opacity: inMonth ? 1 : 0.55,
        '&:last-of-type': { borderRight: 0 },
      }}
    >
      {dateHeader}

      {inMonth ? (
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: summaries.length > 0 ? 44 : 28,
            pb: showAdd ? 2.5 : 0,
          }}
        >
          {summaries.length > 0 ? (
            <Stack spacing={0.5} sx={{ flex: 1 }}>
              {cardTone === 'detail'
                ? weekTeamOrder.length > 0
                  ? weekTeamOrder.map((teamKey) => {
                      const group = groupsByTeamKey.get(teamKey);
                      if (!group?.length) return null;
                      const dropProps = teamDropProps(teamKey, group);
                      return (
                        <GroupedTeamDayCard
                          key={`team-group-${date}-${teamKey}`}
                          summaries={group}
                          teamNumber={group[0] ? teamNumberFor(group[0]) : null}
                          {...teamCardProps}
                          {...dropProps}
                        />
                      );
                    })
                  : [...groupsByTeamKey.entries()].map(([teamKey, group]) => {
                      const dropProps = teamDropProps(teamKey, group);
                      return (
                        <GroupedTeamDayCard
                          key={`team-group-${date}-${teamKey}`}
                          summaries={group}
                          teamNumber={group[0] ? teamNumberFor(group[0]) : null}
                          {...teamCardProps}
                          {...dropProps}
                        />
                      );
                    })
                : summaries.map((summary) => (
                    <QuotationDayCard
                      key={summary.scheduleDocumentId}
                      summary={summary}
                      isCurrent={isActiveScheduleOnDetailCalendar(
                        summary,
                        currentScheduleDocumentId,
                        activeQuotationDocumentId,
                      )}
                      isFocused={summary.scheduleDocumentId === currentScheduleDocumentId}
                      cardTone={cardTone}
                      useScheduleStatusColors={useScheduleStatusColors}
                      teamNumber={teamNumberFor(summary)}
                      onClick={onQuotationClick ? () => onQuotationClick(summary) : undefined}
                    />
                  ))}
            </Stack>
          ) : null}

          {addButton}
        </Box>
      ) : null}
    </Box>
  );
}

export function MonthlyQuotationTeamCalendar({
  monthAnchor,
  teams,
  extraSummariesByDate,
  currentScheduleDocumentId,
  activeQuotationDocumentId,
  cardTone = 'detail',
  useScheduleStatusColors = false,
  issuedWorkOrderIds,
  locked = false,
  onQuotationClick,
  onAddClick,
  onDeleteScheduleClick,
  canDeleteSchedule,
  onEditScheduleClick,
  canEditSchedule,
  onViewScheduleClick,
  onScheduleCardDrop,
  canDragSchedule,
  getDayTestId,
  getAddTestId,
  rootTestId,
  teamOfficerOptions = [],
  canEditTeam,
  onSaveTeamMembers,
  teamMembersSaving = false,
}: MonthlyQuotationTeamCalendarProps) {
  const summariesByDate = useMemo(() => {
    const base = buildQuotationScheduleDaySummariesByDate(teams);
    const merged = new Map(base);
    if (extraSummariesByDate && extraSummariesByDate.size > 0) {
      for (const [date, extraSummaries] of extraSummariesByDate.entries()) {
        const existing = merged.get(date) ?? [];
        const bySchedule = new Map(existing.map((row) => [row.scheduleDocumentId, row]));
        extraSummaries.forEach((row) => {
          if (!bySchedule.has(row.scheduleDocumentId)) {
            bySchedule.set(row.scheduleDocumentId, row);
          }
        });
        merged.set(
          date,
          [...bySchedule.values()].sort((a, b) => a.quotationNumber.localeCompare(b.quotationNumber, 'id')),
        );
      }
    }
    if (!issuedWorkOrderIds?.size) return merged;
    const withIssued = new Map<string, QuotationScheduleDaySummary[]>();
    for (const [date, rows] of merged.entries()) {
      withIssued.set(
        date,
        rows.map((row) => applyBeritaAcaraIssuedFlag(row, issuedWorkOrderIds)),
      );
    }
    return withIssued;
  }, [teams, extraSummariesByDate, issuedWorkOrderIds]);
  const teamNumberByLookupKey = useMemo(
    () => buildMonthlySurveyTeamNumbersByRoster(summariesByDate),
    [summariesByDate],
  );
  const weeks = useMemo(() => monthCalendarWeeks(monthAnchor), [monthAnchor]);
  const todayIso = isoDate(new Date());
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [teamMembersDialog, setTeamMembersDialog] = useState<{
    date: string;
    summary: QuotationScheduleDaySummary;
    cardSummaries: QuotationScheduleDaySummary[];
    teamNumber: number | null;
  } | null>(null);
  const suppressClickAfterDragRef = useRef(false);
  const dndEnabled = Boolean(onScheduleCardDrop);

  function handleCardDragStart(
    event: React.DragEvent,
    fromDate: string,
    summary: QuotationScheduleDaySummary,
  ) {
    if (!dndEnabled) return;
    const payload = encodeScheduleCardDragPayload({
      fromDate,
      scheduleDocumentId: summary.scheduleDocumentId,
    });
    event.dataTransfer.setData(SCHEDULE_CARD_DND_MIME, payload);
    event.dataTransfer.setData('text/plain', payload);
    event.dataTransfer.effectAllowed = 'move';
    suppressClickAfterDragRef.current = false;
    const target =
      (event.currentTarget instanceof Element
        ? event.currentTarget.closest('[data-schedule-draggable="true"]')
        : null) ?? event.currentTarget;
    if (target instanceof HTMLElement) {
      target.style.opacity = '0.55';
    }
  }

  function handleCardDragEnd(event: React.DragEvent) {
    const target =
      (event.currentTarget instanceof Element
        ? event.currentTarget.closest('[data-schedule-draggable="true"]')
        : null) ?? event.currentTarget;
    if (target instanceof HTMLElement) {
      target.style.opacity = '';
    }
    setDropTargetId(null);
    suppressClickAfterDragRef.current = true;
    window.setTimeout(() => {
      suppressClickAfterDragRef.current = false;
    }, 0);
  }

  function handleDragOverTeam(event: React.DragEvent, targetId: string) {
    if (!dndEnabled) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
    if (dropTargetId !== targetId) setDropTargetId(targetId);
  }

  function handleDragLeaveTeam(event: React.DragEvent, targetId: string) {
    const related = event.relatedTarget;
    if (related instanceof Node && event.currentTarget.contains(related)) return;
    setDropTargetId((current) => (current === targetId ? null : current));
  }

  function handleDropOnTeam(
    event: React.DragEvent,
    toDate: string,
    targetTeam: QuotationScheduleDaySummary,
  ) {
    if (!dndEnabled || !onScheduleCardDrop) return;
    event.preventDefault();
    event.stopPropagation();
    setDropTargetId(null);
    if (!isDefinedSurveyTeam(targetTeam)) return;
    const payload = readScheduleCardDragPayload(event.dataTransfer);
    if (!payload) return;
    if (
      payload.fromDate === toDate &&
      payload.scheduleDocumentId === targetTeam.scheduleDocumentId
    ) {
      return;
    }
    const dragged = (summariesByDate.get(payload.fromDate) ?? []).find(
      (row) => row.scheduleDocumentId === payload.scheduleDocumentId,
    );
    if (!dragged) return;
    if (canDragSchedule && !canDragSchedule(dragged)) return;
    onScheduleCardDrop(payload.fromDate, toDate, dragged, targetTeam);
  }

  function handleQuotationClickForDate(date: string, summary: QuotationScheduleDaySummary) {
    if (suppressClickAfterDragRef.current) {
      suppressClickAfterDragRef.current = false;
      return;
    }
    onQuotationClick?.(date, summary);
  }

  function handleEditTeam(
    date: string,
    summary: QuotationScheduleDaySummary,
    cardSummaries: QuotationScheduleDaySummary[],
  ) {
    const teamNumber = resolveMonthlySurveyTeamNumber(date, summary.members, teamNumberByLookupKey);
    const uniqueCards = (() => {
      const byId = new Map<string, QuotationScheduleDaySummary>();
      for (const row of cardSummaries.length > 0 ? cardSummaries : [summary]) {
        const id = row.scheduleDocumentId.trim();
        if (!id || byId.has(id)) continue;
        byId.set(id, row);
      }
      if (!byId.has(summary.scheduleDocumentId.trim())) {
        byId.set(summary.scheduleDocumentId.trim(), summary);
      }
      return [...byId.values()];
    })();
    setTeamMembersDialog({ date, summary, cardSummaries: uniqueCards, teamNumber });
  }

  return (
    <Box
      data-testid={rootTestId}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'grey.100',
        }}
      >
        {SCHEDULE_WEEKDAY_HEADER_LABELS.map((label) => (
          <Box
            key={label}
            sx={{
              py: 1,
              textAlign: 'center',
              borderRight: 1,
              borderColor: 'divider',
              '&:last-of-type': { borderRight: 0 },
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {weeks.map((week, weekIdx) => {
        const weekTeamOrder =
          cardTone === 'detail'
            ? buildWeekTeamSlotOrder(week, summariesByDate, teamNumberByLookupKey)
            : [];
        const useTeamRowAlign = cardTone === 'detail' && weekTeamOrder.length > 0;

        return (
          <Box
            key={`team-cal-week-${weekIdx}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              ...(useTeamRowAlign
                ? {
                    gridTemplateRows: `auto repeat(${weekTeamOrder.length}, auto)`,
                    rowGap: 0.5,
                  }
                : {}),
            }}
          >
            {week.map((date, dayIdx) => (
              <MonthDayCell
                key={date}
                date={date}
                inMonth={isDateInMonth(date, monthAnchor)}
                isToday={date === todayIso}
                summaries={summariesByDate.get(date) ?? []}
                currentScheduleDocumentId={currentScheduleDocumentId}
                activeQuotationDocumentId={activeQuotationDocumentId}
                cardTone={cardTone}
                useScheduleStatusColors={useScheduleStatusColors}
                teamNumberByLookupKey={teamNumberByLookupKey}
                weekTeamOrder={weekTeamOrder}
                dayColumnIndex={dayIdx + 1}
                useTeamRowAlign={useTeamRowAlign}
                locked={locked}
                dropTargetId={dropTargetId}
                onQuotationClick={
                  onQuotationClick
                    ? (summary) => handleQuotationClickForDate(date, summary)
                    : undefined
                }
                onAddClick={onAddClick ? () => onAddClick(date) : undefined}
                onDeleteSchedule={
                  onDeleteScheduleClick
                    ? (summary) => onDeleteScheduleClick(date, summary)
                    : undefined
                }
                canDeleteSchedule={canDeleteSchedule}
                onEditSchedule={
                  onEditScheduleClick
                    ? (summary) => onEditScheduleClick(date, summary)
                    : undefined
                }
                canEditSchedule={canEditSchedule}
                onViewSchedule={
                  onViewScheduleClick
                    ? (summary) => onViewScheduleClick(date, summary)
                    : undefined
                }
                onEditTeam={
                  cardTone === 'detail'
                    ? (summary, cardSummaries) => handleEditTeam(date, summary, cardSummaries)
                    : undefined
                }
                canDragSchedule={dndEnabled ? canDragSchedule ?? canDeleteSchedule : undefined}
                onCardDragStart={
                  dndEnabled
                    ? (event, summary) => handleCardDragStart(event, date, summary)
                    : undefined
                }
                onCardDragEnd={dndEnabled ? handleCardDragEnd : undefined}
                onDragOverTeam={dndEnabled ? handleDragOverTeam : undefined}
                onDragLeaveTeam={dndEnabled ? handleDragLeaveTeam : undefined}
                onDropOnTeam={dndEnabled ? handleDropOnTeam : undefined}
                resolveTeamDropTemplate={
                  dndEnabled
                    ? (teamKey) => findTeamTemplateSummary(teamKey, summariesByDate)
                    : undefined
                }
                dayTestId={getDayTestId?.(date)}
                addTestId={getAddTestId?.(date)}
              />
            ))}
          </Box>
        );
      })}

      <SurveyTeamMembersDialog
        open={Boolean(teamMembersDialog)}
        summary={teamMembersDialog?.summary ?? null}
        teamNumber={teamMembersDialog?.teamNumber}
        officerOptions={teamOfficerOptions}
        canEdit={
          Boolean(
            teamMembersDialog &&
              onSaveTeamMembers &&
              (canEditTeam
                ? canEditTeam(teamMembersDialog.summary)
                : canEditSchedule?.(teamMembersDialog.summary)),
          )
        }
        saving={teamMembersSaving}
        onClose={() => {
          if (!teamMembersSaving) setTeamMembersDialog(null);
        }}
        onSave={
          teamMembersDialog && onSaveTeamMembers
            ? (payload) => {
                onSaveTeamMembers(
                  teamMembersDialog.date,
                  teamMembersDialog.summary,
                  payload,
                  teamMembersDialog.cardSummaries,
                );
                setTeamMembersDialog(null);
              }
            : undefined
        }
      />
    </Box>
  );
}
